import { useCallback, useEffect } from "react";
import axios from "AxiosInstance";
import { useKaiContext } from "./KaiContext";
import { asCategoryList } from "lib/asCategoryList";

function asList(data) {
  return Array.isArray(data) ? data : [];
}

function hasActiveSession() {
  return Boolean(
    localStorage.getItem("sessionId") && localStorage.getItem("merchantId")
  );
}

/**
 * Loads brands, categories, and attributes globally so KAI quick-replies and
 * coaching work on any screen - not only the product edit form.
 */
export default function KaiGlobalCatalogBridge() {
  const { registerCatalogHelpers } = useKaiContext();

  const refreshCatalog = useCallback(async () => {
    if (!hasActiveSession()) return;

    const headers = { sessionId: localStorage.getItem("sessionId") };
    try {
      const [brandsRes, categoriesRes, attributesRes, typesRes] = await Promise.all([
        axios.get("Brand", { headers }),
        axios.get("Category", { headers }),
        axios.get("Attribute", { headers }),
        axios.get("AttributeType", { headers }),
      ]);

      if (!hasActiveSession()) return;

      const brands = asList(brandsRes?.data);
      const categories = asCategoryList(categoriesRes?.data);
      const attributes = asList(attributesRes?.data);
      const attributeTypes = asList(typesRes?.data);

      registerCatalogHelpers({
        brandNames: brands.map((b) => b?.name).filter(Boolean),
        categoryNames: categories.map((c) => c?.name || c?.publicName).filter(Boolean),
        attributeNames: attributes.map((a) => a?.displayName || a?.name).filter(Boolean),
        attributeTypeNames: attributeTypes.map((t) => t?.name).filter(Boolean),
        refreshCatalog,
      });
    } catch {
      registerCatalogHelpers({ refreshCatalog });
    }
  }, [registerCatalogHelpers]);

  useEffect(() => {
    if (!hasActiveSession()) return;

    refreshCatalog();
    const onCatalogChanged = () => {
      if (hasActiveSession()) refreshCatalog();
    };
    window.addEventListener("kai:catalog-changed", onCatalogChanged);
    return () => {
      window.removeEventListener("kai:catalog-changed", onCatalogChanged);
      registerCatalogHelpers({
        brandNames: undefined,
        categoryNames: undefined,
        attributeNames: undefined,
        attributeTypeNames: undefined,
        refreshCatalog: undefined,
      });
    };
  }, [refreshCatalog, registerCatalogHelpers]);

  return null;
}
